import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import {
  catchError,
  map,
  Observable,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenuModule } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { User } from '../models/user.model';
import { PaginationComponent } from '../shared/components/pagination/pagination.component';
import { ToastNotificationComponent } from '../shared/components/toast-notification/toast-notification.component';
import { Permission } from '../shared/constants/permissions';
import { SharedModule } from '../shared/shared.module';
import {
  closeDropdown,
  closeDropdownOnOutsideClick,
  stopEvent,
  toggleDropdown,
} from '../shared/utils/dropdown.util';
import * as fromStore from '../store';
import { ConfirmDeactivateUserDialogComponent } from './dialogs/confirm-deactivate-user-dialog.component';
import { CreateEditUserDialogComponent } from './dialogs/create-edit-user-dialog.component';
import { OktaUserService } from './services/okta-user';
import { UserDialogData } from './user-dialog.model';

@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  imports: [
    NgxPermissionsModule,
    CommonModule,
    MatTableModule,
    MatIconModule,
    MatDialogModule,
    MatTooltipModule,
    MatSortModule,
    MatSnackBarModule,
    RouterModule,
    MatButtonModule,
    MatFormFieldModule,
    MatMenuModule,
    FormsModule,
    SharedModule,
    PaginationComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageUsersComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  private store = inject(Store<fromStore.State>);

  private oktaUserService = inject(OktaUserService);

  private snackBar = inject(MatSnackBar);

  public permissionsService = inject(NgxPermissionsService);

  users$: Observable<User[]> = this.store.select(fromStore.getUsersWithOktaId);

  // Contacts state starts (and resets on reload) with loading: true.
  readonly usersLoading = toSignal(
    this.store.select(fromStore.getContactsLoading),
    { initialValue: true },
  );

  readonly usersLoaded = toSignal(
    this.store.select(fromStore.getContactsLoaded),
    { initialValue: false },
  );

  loggedInUser = signal<User>({} as User);

  dataSource: MatTableDataSource<User>;

  pageSizeOptions: number[] = [10, 25, 50];

  defaultPageSizeOption: number = 10;

  displayedColumns: string[] = [
    'Number',
    'UserInfo',
    'Username',
    'Role',
    'CreatedDate',
    'LastPortalLogin',
    'Actions',
  ];

  portalRoles$ = this.store.select(fromStore.getPortalRoles);

  totalAdmins = signal(0);

  selectedRoleFilter: string = '';

  isRoleFilterOpen = signal(false);

  isPageSizeDropdownOpen = signal(false);

  sortedFilteredUsers = signal<User[]>([]);

  currentPage = signal(1);

  pageSize = signal(this.defaultPageSizeOption);

  totalPages = computed(() =>
    Math.max(1, Math.ceil(this.sortedFilteredUsers().length / this.pageSize())),
  );

  effectivePage = computed(() =>
    Math.min(this.currentPage(), this.totalPages()),
  );

  pagedUsers = computed(() => {
    const start = (this.effectivePage() - 1) * this.pageSize();
    return this.sortedFilteredUsers().slice(start, start + this.pageSize());
  });

  userSearchTerm: string = '';

  canManageUsers = signal(false);

  isDemoAccount = computed(
    () => !!this.loggedInUser()?.Account?.Is_Demo_Account__c,
  );

  Permission = Permission;

  readonly dialog = inject(MatDialog);

  private readonly destroy$ = new Subject<void>();

  private elementRef = inject(ElementRef);

  private dataSourceSubscription?: Subscription;

  permissionList: Array<{ key: string; label: string }> = [
    { key: Permission.MANAGE_USERS, label: 'Manage Users' },
    {
      key: Permission.TICKETING_WRITE,
      label: 'Create Tickets',
    },
    { key: Permission.TICKETING_READ, label: 'View Tickets' },
    {
      key: Permission.BILLING_READ,
      label: 'View Invoices & Billing',
    },
    { key: Permission.ORDERS, label: 'View Orders' },
    { key: Permission.SERVICES, label: 'View Services' },
    { key: Permission.MONITORING, label: 'View Monitoring' },
  ];

  async ngOnInit(): Promise<void> {
    this.store
      .select(fromStore.getContactsLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadContacts());
        }
      });

    this.store
      .select(fromStore.getPortalRolesLoaded)
      .pipe(take(1))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadPortalRoles());
        }
      });

    this.users$.pipe(takeUntil(this.destroy$)).subscribe((users) => {
      this.dataSource = new MatTableDataSource(users);
      this.handleSorting();
      this.updateUserRoleCounts(users);

      // re-apply whatever the user currently has selected/typed
      this.applyUserSearchFilter();

      this.subscribeToDataSource();

      // Reset to first page so filtered results show from top
      this.currentPage.set(1);
    });

    this.store
      .select(fromStore.getCurrentUser)
      .pipe(
        map((user) => {
          this.loggedInUser.set(user);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.canManageUsers.set(
      await this.permissionsService.hasPermission('Manage Users'),
    );
  }

  ngAfterViewInit(): void {
    this.handleSorting();
  }

  ngOnDestroy(): void {
    this.dataSourceSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    closeDropdownOnOutsideClick(this.elementRef, event, this.isRoleFilterOpen);
    closeDropdownOnOutsideClick(
      this.elementRef,
      event,
      this.isPageSizeDropdownOpen,
    );
  }

  toggleRoleFilter(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isRoleFilterOpen);
  }

  selectRoleFilter(roleName: string, event: Event): void {
    stopEvent(event);
    this.selectedRoleFilter = roleName;
    this.applyUserSearchFilter();
    closeDropdown(this.isRoleFilterOpen);
  }

  togglePageSizeDropdown(event: Event): void {
    stopEvent(event);
    toggleDropdown(this.isPageSizeDropdownOpen);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize.set(pageSize);
    this.currentPage.set(1);
    closeDropdown(this.isPageSizeDropdownOpen);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
  }

  private subscribeToDataSource(): void {
    this.dataSourceSubscription?.unsubscribe();
    this.dataSourceSubscription = this.dataSource
      .connect()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => this.sortedFilteredUsers.set([...data]));
  }

  applyUserSearchFilter(): void {
    const term = this.userSearchTerm.trim().toLowerCase();

    this.dataSource.filterPredicate = (data: User, filter: string) => {
      const [searchTerm, roleFilter] = filter.split('||');

      const searchMatch =
        data.FirstName?.toLowerCase().includes(searchTerm) ||
        data.LastName?.toLowerCase().includes(searchTerm) ||
        data.Email?.toLowerCase().includes(searchTerm) ||
        data.OktaUsername__c?.toLowerCase().includes(searchTerm);

      const roleMatch = roleFilter
        ? data.Portal_Role__r?.Name === roleFilter
        : true;

      return searchMatch && roleMatch;
    };

    this.dataSource.filter = `${term}||${this.selectedRoleFilter}`;
    this.currentPage.set(1);
  }

  handleSorting(): void {
    if (!this.dataSource) {
      return;
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }

    // Sorting logic for nested properties
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'Role':
          return item?.Portal_Role__r?.Name || '';
        case 'UserInfo':
          return item.LastName || '';
        case 'Username':
          return item.OktaUsername__c || '';
        case 'LastPortalLogin':
          return item?.Last_Portal_Login_At__c || '';
        default:
          return item[property];
      }
    };
  }

  onCreateEditUser(user?: User): void {
    const dialogRef = this.openCreateEditUserDialog(user);

    this.handleDialogResult(dialogRef, () => {
      this.reloadContacts();
    });
  }

  onDeactivateUser(user: User): void {
    const dialogRef = this.openSuspendUserDialog(user);

    this.handleDialogResult(dialogRef, () => {
      this.deactivateUser(user);
    });
  }

  onResendInvite(user: User): void {
    this.oktaUserService
      .resendActivationEmail(user.OktaUsername__c)
      .pipe(
        takeUntil(this.destroy$),
        tap(() =>
          this.showSnackBar('Activation email resent successfully.', 'success'),
        ),
        catchError((err) => {
          this.showSnackBar(
            err?.message ??
              'Failed to resend activation email. Please try again.',
            'error',
          );
          return [];
        }),
      )
      .subscribe();
  }

  onUnlockUser(user: User): void {
    this.oktaUserService
      .unlockUser(user.OktaUsername__c)
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.showSnackBar('User unlocked successfully.', 'success')),
        catchError((err) => {
          this.showSnackBar(
            err?.message ?? 'Failed to unlock user. Please try again.',
            'error',
          );
          return [];
        }),
      )
      .subscribe();
  }

  onSendPasswordReset(user: User): void {
    this.oktaUserService
      .forgotPassword(user.OktaUsername__c)
      .pipe(
        takeUntil(this.destroy$),
        tap(() =>
          this.showSnackBar(
            'Password reset email sent successfully.',
            'success',
          ),
        ),
        catchError(() => {
          this.showSnackBar(
            'Failed to send password reset email. Please try again.',
            'error',
          );
          return [];
        }),
      )
      .subscribe();
  }

  private openCreateEditUserDialog(
    user?: User,
  ): MatDialogRef<CreateEditUserDialogComponent, User> {
    return this.dialog.open(CreateEditUserDialogComponent, {
      disableClose: true,
      autoFocus: false,
      restoreFocus: true,
      data: {
        ...user,
        AccountId: this.loggedInUser().AccountId,
        Account: {
          Name: this.loggedInUser().Account.Name,
          Is_Demo_Account__c: this.loggedInUser().Account?.Is_Demo_Account__c,
        },
        TotalAdmins: this.totalAdmins(),
      } as UserDialogData,
    });
  }

  private openSuspendUserDialog(
    user: User,
  ): MatDialogRef<ConfirmDeactivateUserDialogComponent, User> {
    return this.dialog.open(ConfirmDeactivateUserDialogComponent, {
      width: '400px',
      autoFocus: false,
      restoreFocus: true,
      data: user,
    });
  }

  private handleDialogResult(
    dialogRef: MatDialogRef<
      CreateEditUserDialogComponent | ConfirmDeactivateUserDialogComponent,
      User | boolean
    >,
    onSuccess: () => void,
  ): void {
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (
          (typeof result === 'object' && result !== null) ||
          result === true
        ) {
          onSuccess();
        }
      });
  }

  private deactivateUser(user: User): void {
    this.oktaUserService
      .deactivateUser({ login: user.OktaUsername__c, sfUserId: user.Id })
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.showSnackBar('User successfully removed.', 'success');
          this.reloadContacts();
        }),
        catchError(() => {
          this.showSnackBar('Error removing user. Please try again.', 'error');
          return [];
        }),
      )
      .subscribe();
  }

  private reloadContacts(): void {
    this.store.dispatch(new fromStore.ClearContacts());
    this.store.dispatch(new fromStore.LoadContacts());
  }

  private showSnackBar(message: string, type: 'success' | 'error'): void {
    this.snackBar.openFromComponent(ToastNotificationComponent, {
      duration: 7000,
      panelClass: ['toast-panel'],
      data: { message, type },
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }

  private updateUserRoleCounts(users: User[]): void {
    this.totalAdmins.set(
      users.filter((user) => user.Portal_Role__r?.Name === 'Admin').length,
    );
  }
}
